"use client"
import { useEffect, useRef, useState } from "react"
import { FileCheck, Zap, ShieldCheck, Users, BarChart3, Layers } from "lucide-react"

type Step = {
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  side: "left" | "right"
}

const steps: Step[] = [
  {
    title: "Automated, AI-Driven Claims Adjudication",
    description: "Cut manual work and process claims in record time with intelligent, rules-based automation.",
    icon: FileCheck,
    side: "left",
  },
  {
    title: "Real-Time Synchronization Across Departments",
    description: "Keep underwriting, claims, and care perfectly aligned with instant system updates. No delays, no data silos.",
    icon: Zap,
    side: "right",
  },
  {
    title: "Audit-Ready Reporting, Instantly",
    description: "Generate compliant, regulator-ready reports in seconds and glide through audits with confidence.",
    icon: ShieldCheck,
    side: "left",
  },
  {
    title: "Unified Provider & Member Management",
    description: "Manage providers, members, authorizations, and payments from one intelligent, connected platform.",
    icon: Users,
    side: "right",
  },
  {
    title: "Custom Dashboards for Smarter Decisions",
    description: "See the story behind your data. Track KPIs, surface trends, and act with precision.",
    icon: BarChart3,
    side: "left",
  },
  {
    title: "Modular by Design, Scalable by Nature",
    description: "Start with what you need today and expand effortlessly as your organization grows.",
    icon: Layers,
    side: "right",
  },
]

const iconColors = [
  "text-[#1a85ca]", // primary
  "text-[#EA3056]", // secondary
  "text-[#34D1BE]", // accent teal
  "text-[#1a85ca]", // azure
  "text-[#0630a0]", // deep blue
  "text-[#A855F7]", // purple
]

function BranchCard({ icon: Icon, title, description, index }: { icon: Step["icon"]; title: string; description: string; index: number }) {
  return (
    <div className="rounded-2xl bg-white/95 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 p-5 sm:p-6 flex flex-col md:flex-row items-center gap-4 float-card" style={{ animationDelay: `${(index % 3) * 2}s` }}>
      <div className="h-12 w-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
        <Icon className={`h-6 w-6 ${iconColors[index % iconColors.length]}`} />
      </div>
      <div className="text-center md:text-left">
        <div className="text-base sm:text-lg font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-600 mt-1">{description}</div>
      </div>
    </div>
  )
}

export function ProblemSolutionSection() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const update = () => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const vh = window.innerHeight
      const start = vh * 0.15
      const total = Math.max(rect.height - start, 1)
      const scrolled = Math.min(Math.max(vh - rect.top - start, 0), total)
      const p = Math.min(Math.max(scrolled / total, 0), 1)
      setProgress(p)
    }
    update()
    window.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [])
  return (
    <section id="octohealth-solution" className="py-16 sm:py-20 bg-white relative overflow-hidden">
      {/* Decorative blurred shapes for subtle depth */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#1a85ca]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#EA3056]/10 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-center text-[1.53rem] sm:text-[1.224rem] lg:text-[2.754rem] font-light text-gray-900">The OctoHealth Solution</h2>
          <p className="text-[0.9rem] sm:text-lg text-gray-600 max-w-3xl mx-auto">
            OctoHealth ERP unifies every part of your health insurance business into one secure, data-driven system. Powered by AI, it streamlines workflows, enhances accuracy, and delivers insights that drive smarter, faster decisions.
          </p>
        </div>

        {/* Timeline wrapper with vertical line (Desktop only) */}
        <div ref={containerRef} className="relative">
          {/* Base track - Desktop only */}
          <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[0.1%] rounded-full bg-gray-200" />
          {/* Filled trunk in primary color (descending from top) - Desktop only */}
          <div
            className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 w-[0.1%] rounded-full bg-[#1a85ca] shadow-[0_0_12px_2px_rgba(8,64,207,0.25)] transition-[height] duration-300"
            style={{ height: `${Math.round(progress * 100)}%` }}
            aria-hidden="true"
          />

          {/* Mobile Layout - Single Column */}
          <div className="block md:hidden space-y-6">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                {/* Mobile numbered chip */}
                <div className="flex justify-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-white shadow-md ring-1 ring-gray-200 flex items-center justify-center text-gray-700 font-semibold">
                    {i + 1}
                  </div>
                </div>
                {/* Mobile card */}
                <BranchCard index={i} icon={step.icon} title={step.title} description={step.description} />
              </div>
            ))}
          </div>

          {/* Desktop Layout - Two Column with Timeline */}
          <div className="hidden md:block space-y-8 sm:space-y-10">
            {steps.map((step, i) => (
              <div key={i} className="relative grid md:grid-cols-[1fr_80px_1fr] items-center gap-6">
                {/* Left card */}
                <div className={step.side === "left" ? "relative" : "hidden md:block"}>
                  {step.side === "left" && (
                    <BranchCard index={i} icon={step.icon} title={step.title} description={step.description} />
                  )}
                  {step.side === "left" && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-[-42px] h-px w-[84px] bg-gray-200" />
                  )}
                </div>

                {/* Center numbered chip */}
                <div className="relative flex justify-center">
                  <div className="h-10 w-10 rounded-full bg-white shadow-md ring-1 ring-gray-200 flex items-center justify-center text-gray-700 font-semibold">
                    {i + 1}
                  </div>
                </div>

                {/* Right card */}
                <div className={step.side === "right" ? "relative" : "hidden md:block"}>
                  {step.side === "right" && (
                    <BranchCard index={i} icon={step.icon} title={step.title} description={step.description} />
                  )}
                  {step.side === "right" && (
                    <div className="absolute top-1/2 -translate-y-1/2 left-[-42px] h-px w-[84px] bg-gray-200" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Global animations for floating cards */}
      <style jsx global>{`
        @keyframes slowFloat {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .float-card { animation: slowFloat 12s ease-in-out infinite; will-change: transform; }
      `}</style>
    </section>
  )
}
